import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Network, X, Plus } from 'lucide-react';
import { EntitySelectorDialog } from './EntitySelectorDialog';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

interface EntityLinkSelectorProps {
  allowedTypes: EntityType[];
  onLinksChange: (entities: SelectedEntity[]) => void;
  initialLinks?: SelectedEntity[];
}

export function EntityLinkSelector({
  allowedTypes,
  onLinksChange,
  initialLinks = [],
}: EntityLinkSelectorProps) {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedEntities, setSelectedEntities] = useState<SelectedEntity[]>(initialLinks);

  const handleSelectEntities = (entityType: EntityType, entityIds: string[]) => {
    // We need to fetch entity names - for now use placeholders
    const newEntities = entityIds.map(id => ({
      id,
      type: entityType,
      name: `${entityType}-${id.slice(0, 8)}`,
    }));
    
    const updated = [...selectedEntities, ...newEntities];
    setSelectedEntities(updated);
    onLinksChange(updated);
  };

  const handleRemoveEntity = (entityId: string) => {
    const updated = selectedEntities.filter(e => e.id !== entityId);
    setSelectedEntities(updated);
    onLinksChange(updated);
  };

  const groupedEntities = selectedEntities.reduce((acc, entity) => {
    if (!acc[entity.type]) {
      acc[entity.type] = [];
    }
    acc[entity.type].push(entity);
    return acc;
  }, {} as Record<EntityType, SelectedEntity[]>);

  return (
    <>
      <Separator />
      
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Network className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">Link to Related Entities (Optional)</h4>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setSelectorOpen(true)}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Links
        </Button>

        {Object.keys(groupedEntities).length > 0 && (
          <div className="space-y-2">
            {Object.entries(groupedEntities).map(([type, entities]) => (
              <div key={type}>
                <p className="text-xs text-muted-foreground mb-1 capitalize">
                  {type.replace('_', ' ')}s:
                </p>
                <div className="flex flex-wrap gap-1">
                  {entities.map(entity => (
                    <Badge key={entity.id} variant="secondary" className="gap-1">
                      {entity.name}
                      <button
                        type="button"
                        onClick={() => handleRemoveEntity(entity.id)}
                        className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <EntitySelectorDialog
        open={selectorOpen}
        onOpenChange={setSelectorOpen}
        onSelect={handleSelectEntities}
        allowedTypes={allowedTypes}
        multiSelect={true}
        title="Select Entities to Link"
      />
    </>
  );
}
